import { useEffect, useRef } from "react";
import useIsTabVisible from "./useIsTabVisible";

const startChainedPoll = ({ run, intervalMs, firstDelayMs }) => {
   let isCancelled = false;
   let timeoutId;
   const tick = async () => {
      try {
         await run();
      } catch (error) {
         console.error("usePolling callback failed", error);
      } finally {
         if (!isCancelled) timeoutId = setTimeout(tick, intervalMs);
      }
   };
   timeoutId = setTimeout(tick, firstDelayMs);
   return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
   };
};

/**
 * Calls the callback every intervalMs, scheduling the next tick only after the previous one settles.
 * Pauses while the tab is hidden and runs once right away when it becomes visible again.
 * @param {Object} options
 * @param {number} options.intervalMs - Delay between the end of one call and the start of the next
 * @param {Function} options.callback - Sync or async function to run on each tick
 * @param {boolean} [options.enabled=true] - Polling is off while false
 */
const usePolling = ({ intervalMs, callback, enabled = true }) => {
   const callbackRef = useRef(callback);
   const isTabVisible = useIsTabVisible();
   const wasHiddenRef = useRef(false);

   // refresh ref each render so each tick invokes the freshest callback
   useEffect(() => {
      callbackRef.current = callback;
   });

   useEffect(() => {
      const wasHidden = wasHiddenRef.current;
      wasHiddenRef.current = !isTabVisible;
      if (!enabled || !intervalMs || !isTabVisible) return undefined;
      const firstDelayMs = wasHidden ? 0 : intervalMs;
      return startChainedPoll({ run: () => callbackRef.current?.(), intervalMs, firstDelayMs });
   }, [enabled, intervalMs, isTabVisible]);
};

export default usePolling;
